import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Dropdown from '../Dropdown';
import { AssignProcessToFolderModal, EditprocessModal, GenericModal, PublishModal } from '../../views/modal';
import AddProcessOwnerModal from '../../views/modal/addProcessOwnerModal';
import AddDataCollectionCoOwnerModal from '../../views/modal/addDataCollectionCoOwnerModal';
import { useAppSelector } from '../../stores';
import { FamlaServices } from '../../services';
import { slice } from '../../utils';
import { Routes } from '../../routes';
import { iProject } from '../../interfaces';
import { iNewDataProcess, iProcess } from '../type';
import { Images } from '../../constants';
import Badge from '../badge';
import { navigateToInterviewWithFallback } from '../../utils/smartNavigation';
import { navigateToLatestManualMapping } from '../../utils/mappingAccess';
import { formatProcessCardUpdatedAt, getProcessModifiedTimestamp } from '../../utils/formatDate';
import { getProcessCardDetailLine, isManualProcess } from '../../utils/processTypeUtils';
import ProcessMethodBadge from './ProcessMethodBadge';
import { trackUiButtonEventAsync } from '../../services/api/uiButtonStatsService';


interface UnifiedCardProps {
    processName: string;
    description: string;
    onClick?: () => void;
    triggerModal?: boolean;
    modalTarget?: string;
    bgImage?: string;
    classIcon?: string;
    t: (key: string, options?: Record<string, unknown>) => string;
    thisProcessHasABadge: boolean;
    theBadgeHasAValue: boolean;
    badgeValue: number;
    owner: string;
    thisProcessHasAOwner: boolean;
    route: string;
    deleteProcess: () => void | boolean | Promise<void | boolean>;
    processId: string;
    folder: boolean;
    setProject: (form: iNewDataProcess) => void;
    setProcess: (form: iNewDataProcess) => void;
    usage?: string;
    index?: number;
    elementType?: 'process' | 'folder' | 'popular' | 'dataCollect';
    /** Collecte data (bibliothèque) : renommage sans modal process */
    onDataCollectRenameRequest?: () => void | Promise<void>;
    notifFolder?: boolean;
    isSample?: boolean;
    /** Mise en page type maquette bibliothèque (cartes sombres, grille) */
    variant?: 'default' | 'library';
    /** Statut calculé du processus (new / ongoing / completed) */
    processStatus?: 'new' | 'ongoing' | 'completed';
    /**
     * Horodatage de dernière modification (ISO ou ms). Si absent, le parent peut omettre :
     * on tente aussi `createdAt` / champs camelCase via getProcessModifiedTimestamp en interne si vous passez `processSnapshot`.
     */
    updatedAt?: string | number;
    /** Repli si pas de mise à jour (ex. created_at) */
    createdAt?: string | number;
    /** Objet process brut (optionnel) — permet de lire updatedAt/createdAt camelCase depuis l'API */
    processSnapshot?: Record<string, unknown>;
    /** Clé d’événement stats (optionnel) — ex. ouvrir le processus depuis l’accueil en mode recherche. */
    uiStatsOpenProcessKey?: string;
    /** Clé d’événement pour le menu ⋯ (optionnel). */
    uiStatsRowMenuKey?: string;
    /** Collecte data : ouverture session contributeur /r/:token au lieu du workspace owner */
    dataCollectContributorToken?: string | null;
    /** Après ajout co-propriétaire collecte (ex. rafraîchir la liste) */
    onDataCollectCoOwnerAdded?: () => void;
    /** Contributeur invité : menu « Retirer ma contribution » */
    canWithdrawDataCollectContribution?: boolean;
    onWithdrawDataCollectContribution?: () => void | boolean | Promise<void | boolean>;
    /**
     * Afficher « Add process owner ». Si absent : aligné sur les droits API (porteur + co-propriétaire).
     */
    canAddProcessOwner?: boolean;
    /**
     * Marquer final, renommer, supprimer. Si absent : `GET /process/my/role` (is_process_manager : porteur ou co-propriétaire).
     */
    canRunOwnerMenuActions?: boolean;
}

const UnifiedCard: React.FC<UnifiedCardProps> = (props) => {
    const navigate = useNavigate();
    const {
        processName,
        description,
        onClick,
        triggerModal = false,
        modalTarget = '',
        bgImage,
        classIcon,
        t,
        thisProcessHasABadge,
        theBadgeHasAValue,
        badgeValue,
        owner,
        thisProcessHasAOwner,
        // 'key' est géré automatiquement par React - ne pas le destructurer
        route,
        deleteProcess,
        processId,
        folder,
        setProject,
        setProcess,
        usage,
        index,
        elementType,
        notifFolder,
        isSample,
        variant = 'default',
        updatedAt,
        createdAt,
        processSnapshot,
        processStatus,
        onDataCollectRenameRequest,
        uiStatsOpenProcessKey,
        uiStatsRowMenuKey,
        canAddProcessOwner: canAddProcessOwnerProp,
        canRunOwnerMenuActions: canRunOwnerMenuActionsProp,
        dataCollectContributorToken,
        onDataCollectCoOwnerAdded,
        canWithdrawDataCollectContribution,
        onWithdrawDataCollectContribution,
    } = props;

    const role = useAppSelector(state => state.role.role);
    const users = useAppSelector((state) => state.auth);
    const [interviewId, setInterviewId] = useState<string | undefined>();
    const [isNavigating, setIsNavigating] = useState(false);
    const [isSwitching, setIsSwitching] = useState(false);
    /** Une seule modale delete montée à la demande (évite N× GenericModal au chargement de la grille bibliothèque). */
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [showCoOwnerModal, setShowCoOwnerModal] = useState(false);
    /** Porteur ou co-propriétaire — utilisé si `canRunOwnerMenuActions` / `canAddProcessOwner` non fournis */
    const [processManageRights, setProcessManageRights] = useState<boolean | null>(null);

    useEffect(() => {
        if (canRunOwnerMenuActionsProp !== undefined && canAddProcessOwnerProp !== undefined) {
            setProcessManageRights(null);
            return;
        }
        const isProcessCard =
            !folder &&
            thisProcessHasAOwner &&
            Boolean(processId && String(processId).trim() !== '') &&
            elementType !== 'dataCollect' &&
            elementType !== 'popular';
        if (!isProcessCard) {
            setProcessManageRights(null);
            return;
        }
        let cancelled = false;
        (async () => {
            const data = await FamlaServices(
                'api_host',
                `/process/my/role/${processId}`,
                'GET',
                '',
                users.access_token,
            );
            if (cancelled) return;
            const ok = data.status === 200 && data.body?.data?.is_process_manager === true;
            setProcessManageRights(ok);
        })();
        return () => {
            cancelled = true;
        };
    }, [
        folder,
        thisProcessHasAOwner,
        processId,
        elementType,
        users.access_token,
        canRunOwnerMenuActionsProp,
        canAddProcessOwnerProp,
    ]);

    useEffect(() => {
        if (elementType === 'dataCollect') return;
        const getInterview = async () => {
            const data = await FamlaServices("api_host", `/interview/process/${processId}`, "GET", "", users.access_token);
            if (data.status === 200) {
                setInterviewId(data.body.data._id);
            }
        };
        // Ne charger l'interview que si processId existe ET que le process a un owner
        if (thisProcessHasAOwner && processId && processId.trim() !== '') {
            getInterview();
        }
    }, [elementType, processId, thisProcessHasAOwner, users.access_token]);

    useEffect(() => {
        setShowDeleteModal(false);
        setShowCoOwnerModal(false);
    }, [processId]);

    const getRoute = (): string => {
        if (thisProcessHasAOwner && !folder && interviewId) {
            return `${route}${interviewId}`;
        } else if (folder) {
            return `${Routes.PROCESS_BY_FOLDER}${processId}`;
        }
        return route;
    };

    const handleCardClick = async () => {
        if (uiStatsOpenProcessKey) {
            await trackUiButtonEventAsync(uiStatsOpenProcessKey);
        }
        // [NAVIGATION FOLDERS] Les dossiers utilisent la navigation classique
        if (folder) {
            navigate(`${Routes.PROCESS_BY_FOLDER}${processId}`);
            return;
        }

        // [NAVIGATION POPULAR] Les éléments populaires utilisent la route directe
        if (elementType === 'popular') {
            if (onClick) {
                onClick();
                return;
            }
            navigate(getRoute());
            return;
        }

        if (elementType === 'dataCollect' && processId) {
            if (dataCollectContributorToken && String(dataCollectContributorToken).trim()) {
                navigate(`/r/${String(dataCollectContributorToken).trim()}`);
                return;
            }
            navigate(`/process/data-collect/${processId}`);
            return;
        }

        // [NAVIGATION INTERVIEWS] Navigation intelligente avec switch automatique
        if (thisProcessHasAOwner && processId && processId.trim() !== '') {
            if (isNavigating) return;

            setIsNavigating(true);

            try {
                if (isManualProcess(processSnapshot?.type)) {
                    await navigateToLatestManualMapping({
                        processId,
                        accessToken: users.access_token,
                        navigate,
                    });
                    return;
                }

                console.log('🎯 [UnifiedCard] Starting smart navigation to process:', processId);

                const success = await navigateToInterviewWithFallback(
                    processId,
                    users.access_token,
                    navigate,
                    {
                        onSwitchStart: () => {
                            console.log('🔄 [UnifiedCard] Company switch started');
                            setIsSwitching(true);
                        },
                        onSwitchComplete: () => {
                            console.log('✅ [UnifiedCard] Company switch completed');
                            setIsSwitching(false);
                        },
                        onError: (error: string) => {
                            console.error('❌ [UnifiedCard] Navigation error:', error);
                            setIsNavigating(false);
                            setIsSwitching(false);
                            
                            // [FALLBACK LEGACY] Si interviewId disponible, navigation directe
                            if (interviewId) {
                                console.log('🔄 [UnifiedCard] Using legacy fallback with interviewId:', interviewId);
                                navigate(Routes.PROCESS_INTERVIEW + interviewId);
                            }
                        },
                        updateSelection: setInterviewId
                    }
                );

                if (success) {
                    console.log('✅ [UnifiedCard] Navigation successful');
                } else if (interviewId) {
                    console.log('⚠️ [UnifiedCard] Smart navigation failed, using direct navigation');
                    navigate(Routes.PROCESS_INTERVIEW + interviewId);
                }

            } catch (error) {
                console.error('❌ [UnifiedCard] Unexpected navigation error:', error);
                
                // [FALLBACK D'URGENCE] Navigation directe en cas d'erreur critique
                if (interviewId) {
                    navigate(Routes.PROCESS_INTERVIEW + interviewId);
                }
            } finally {
                setIsNavigating(false);
                setIsSwitching(false);
            }
        } else {
            // [FALLBACK FINAL] Navigation normale pour cas edge
            navigate(getRoute());
        }
    };
    const handleDropdownClick = (event: React.MouseEvent) => {
        event.stopPropagation();
    };

    const onMenuButtonStats = uiStatsRowMenuKey
        ? () => {
              void trackUiButtonEventAsync(uiStatsRowMenuKey);
          }
        : undefined;

    const backgroundStyle = bgImage
        ? { backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : {
              backgroundImage:
                  elementType === 'process'
                      ? `url(${Images.process})`
                      : elementType === 'folder'
                        ? `url(${Images.folder})`
                        : elementType === 'dataCollect'
                          ? ''
                          : '',
          };

    const displayName = processName === "Default" ? t('views.processView.defaultFolder') : processName;

    const resolvedModifiedTs =
        updatedAt ??
        createdAt ??
        (processSnapshot ? getProcessModifiedTimestamp(processSnapshot) : undefined);

    const libraryUpdatedLabel =
        variant === 'library' && resolvedModifiedTs !== undefined && resolvedModifiedTs !== null && String(resolvedModifiedTs) !== ''
            ? formatProcessCardUpdatedAt(resolvedModifiedTs)
            : '';

    const isDataCollectCard = elementType === 'dataCollect';
    const libraryProcessDescription =
        variant === 'library' && !isDataCollectCard && (elementType === 'process' || !elementType) && processSnapshot
            ? getProcessCardDetailLine({ description, type: processSnapshot.type }, t)
            : description;
    const canWithdrawContributorMenu = Boolean(
        isDataCollectCard && canWithdrawDataCollectContribution && onWithdrawDataCollectContribution,
    );
    const runOwnerMenuActions =
        canRunOwnerMenuActionsProp !== undefined
            ? canRunOwnerMenuActionsProp
            : processManageRights !== null
              ? processManageRights
              : owner === t('words.me');
    const dropdownAllowed =
        (role !== 'WORKER' || owner === t('words.me') || processManageRights === true) &&
        processName !== 'Default' &&
        thisProcessHasAOwner &&
        (!isDataCollectCard || runOwnerMenuActions || canWithdrawContributorMenu);
    /** Marquer final / renommer process / supprimer : masqués si non gestionnaire process. Les dossiers gardent renommer/supprimer. */
    const showMarkRenameDeleteProcess =
        folder || runOwnerMenuActions;
    const showAddProcessOwner =
        canAddProcessOwnerProp !== undefined
            ? canAddProcessOwnerProp
            : runOwnerMenuActions &&
              !folder &&
              !isDataCollectCard &&
              (elementType === 'process' || elementType === undefined);
    const collectIsDraft =
        isDataCollectCard && processSnapshot && String(processSnapshot.status ?? '').toLowerCase() === 'draft';
    const showDataCollectAddCoOwner = Boolean(isDataCollectCard && runOwnerMenuActions && !folder);

    const libraryProcessStatusResolved =
        isDataCollectCard && processSnapshot
            ? String(processSnapshot.status ?? '').toLowerCase() === 'closed'
                ? 'completed'
                : String(processSnapshot.status ?? '').toLowerCase() === 'draft'
                  ? 'new'
                  : 'ongoing'
            : (processStatus ?? 'ongoing');

    const libraryModals = (
        <>
            {!folder && elementType === 'process' && processId && (
                <AddProcessOwnerModal processId={processId} modalId={`addProcessOwner${processId}`} />
            )}
            {!isDataCollectCard && processName && processId && (
                <AssignProcessToFolderModal processId={processId} processName={processName} />
            )}
            {!isDataCollectCard && processName && processId && (
                <EditprocessModal
                    folder={folder}
                    processId={processId}
                    processName={processName}
                    description={description}
                    setProject={setProject}
                    setProcess={setProcess}
                />
            )}
            {isDataCollectCard && processId && runOwnerMenuActions && showCoOwnerModal && (
                <AddDataCollectionCoOwnerModal
                    collectionId={processId}
                    modalId={`addDataCollectionCoOwner${processId}`}
                    onSuccess={onDataCollectCoOwnerAdded}
                    autoShow
                    onHidden={() => setShowCoOwnerModal(false)}
                />
            )}
            {processId && showDeleteModal && (!isDataCollectCard || runOwnerMenuActions) && (
                <GenericModal
                    title={t('words.delete')}
                    actionButton={t('words.delete')}
                    action={async () => {
                        const r = await Promise.resolve(deleteProcess());
                        if (r === false) return false;
                        return true;
                    }}
                    sentence={
                        isDataCollectCard
                            ? collectIsDraft
                                ? t('form.collectWorkspace.deleteDataCollectionConfirm', {
                                      defaultValue: 'Delete this draft?',
                                  })
                                : t('form.collectWorkspace.deleteDataCollectionConfirmActive', {
                                      defaultValue:
                                          'Delete this collection and all responses? This cannot be undone.',
                                  })
                            : t('views.processView.areYouSure')
                    }
                    processId={processId}
                    modalDomId={isDataCollectCard ? `famla-dc-delete-${processId}` : undefined}
                    autoShow
                    onHidden={() => setShowDeleteModal(false)}
                />
            )}
            {processId && showWithdrawModal && canWithdrawContributorMenu && onWithdrawDataCollectContribution && (
                <GenericModal
                    title={t('form.collectWorkspace.withdrawMyContribution', {
                        defaultValue: 'Remove my contribution',
                    })}
                    actionButton={t('form.collectWorkspace.withdrawMyContribution', {
                        defaultValue: 'Remove my contribution',
                    })}
                    action={async () => {
                        const r = await Promise.resolve(onWithdrawDataCollectContribution());
                        if (r === false) return false;
                        return true;
                    }}
                    sentence={t('form.collectWorkspace.withdrawMyContributionConfirm', {
                        defaultValue:
                            'Remove your contribution from this collection? Your responses and messages will be deleted.',
                    })}
                    processId={processId}
                    modalDomId={`famla-dc-withdraw-${processId}`}
                    autoShow
                    onHidden={() => setShowWithdrawModal(false)}
                />
            )}
            {processId && !isDataCollectCard && <PublishModal processId={processId} />}
        </>
    );

    if (variant === 'library') {
        const navOverlay = isNavigating && (
            <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.45)',
                    zIndex: 10,
                    borderRadius: 'inherit',
                }}
            >
                <div className="d-flex align-items-center gap-2 text-white">
                    <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <small>
                        {isSwitching
                            ? t('navigation.switchingCompany') || '…'
                            : t('navigation.loading') || '…'}
                    </small>
                </div>
            </div>
        );

        if (folder) {
            return (
                <>
                    <div
                        className="lib-card-wrap mt-0"
                        style={{
                            cursor: isNavigating ? 'wait' : 'pointer',
                            opacity: isNavigating ? 0.85 : 1,
                            position: 'relative',
                        }}
                        onClick={handleCardClick}
                    >
                        <div className="lib-folder-card" role="button">
                            {navOverlay}
                            <div className="lib-folder-head">
                                <div className="lib-folder-title-row">
                                    <svg width={16} height={16} viewBox="0 0 16 16" fill="none" aria-hidden>
                                        <path
                                            d="M2 4C2 3.4 2.4 3 3 3H6.5L8 5H13C13.6 5 14 5.4 14 6V12C14 12.6 13.6 13 13 13H3C2.4 13 2 12.6 2 12V4Z"
                                            stroke="currentColor"
                                            strokeWidth="1.2"
                                        />
                                    </svg>
                                    <span className="lib-folder-name">{slice(displayName, 32)}</span>
                                </div>
                                <div className="d-flex align-items-center gap-1">
                                    <span className="lib-folder-badge">{badgeValue ?? 0}</span>
                                    {dropdownAllowed && (
                                        <Dropdown
                                            dropName=""
                                            dropdownType="dropdown dropstart"
                                            iconClassName="fa fa-ellipsis-h"
                                            buttonDropClassName="btn btn-secondary bg-transparent border-0 p-0"
                                            styleButton={{}}
                                            onMenuButtonClick={onMenuButtonStats}
                                            action3={`${t('words.organize')}`}
                                            theRouteOfAction4=""
                                            deleteProcess={deleteProcess}
                                            action1=""
                                            theRouteOfAction5=""
                                            dataBsTarget={`#asignProcessToFolder${processId}`}
                                            routeFaq=""
                                            edit={`${t('words.rename')}`}
                                            dataBsTargetEdit={`#edit${processId}`}
                                            publish=""
                                            dataBsTargetPublish={`#publish${processId}`}
                                            onClick={handleDropdownClick}
                                            action7={t('words.delete')}
                                            action7OnClick={() => setShowDeleteModal(true)}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="lib-folder-sub">
                                {t('views.processView.libraryFolderProcessCount', { count: badgeValue || 0 })}
                            </div>
                            {notifFolder && (
                                <span className="lib-pill-new" style={{ marginTop: 2 }}>
                                    {t('words.new')}
                                </span>
                            )}
                        </div>
                    </div>
                    {libraryModals}
                </>
            );
        }

        if (elementType === 'popular') {
            return (
                <>
                    <div
                        className="lib-card-wrap mt-0"
                        style={{ cursor: 'pointer', position: 'relative' }}
                        onClick={handleCardClick}
                    >
                        <div className="lib-insight-card" role="button">
                            <div className="lib-insight-title">{slice(displayName, 44)}</div>
                            {description?.length > 0 && (
                                <div className="lib-insight-desc" title={description}>
                                    {slice(description, 90)}
                                </div>
                            )}
                        </div>
                    </div>
                    {libraryModals}
                </>
            );
        }

        return (
            <>
                <div
                    className="lib-card-wrap mt-0"
                    style={{
                        cursor: isNavigating ? 'wait' : 'pointer',
                        opacity: isNavigating ? 0.85 : 1,
                        position: 'relative',
                    }}
                    onClick={handleCardClick}
                >
                    <div
                        className={`lib-process-card${isDataCollectCard ? ' lib-process-card--datacollect' : ''}`}
                        role="button"
                    >
                        {navOverlay}
                        <div className="lib-p-head">
                            <div className="lib-p-name-with-flag">
                                <div className="lib-p-name" title={displayName}>
                                    {slice(displayName, 42)}
                                </div>
                                {isDataCollectCard ? (
                                    <span className="lib-p-type-processmap">
                                        {t('views.processView.homeTypeDataCollect', { defaultValue: 'Data collection' })}
                                    </span>
                                ) : elementType === 'process' || !elementType ? (
                                    <ProcessMethodBadge processType={processSnapshot?.type} variant="library" />
                                ) : null}
                            </div>
                            <div className="lib-menu">
                                {dropdownAllowed ? (
                                    <Dropdown
                                        dropName=""
                                        dropdownType="dropdown dropstart"
                                        iconClassName="fa fa-ellipsis-h"
                                        buttonDropClassName="btn btn-secondary bg-transparent border-0 p-0"
                                        styleButton={{}}
                                        onMenuButtonClick={onMenuButtonStats}
                                        addProcessOwner={
                                            showDataCollectAddCoOwner
                                                ? t('views.processView.addCollectionOwner')
                                                : showAddProcessOwner && !isDataCollectCard
                                                  ? t('views.processView.addProcessOwner')
                                                  : undefined
                                        }
                                        addProcessOwnerOnClick={
                                            showDataCollectAddCoOwner ? () => setShowCoOwnerModal(true) : undefined
                                        }
                                        addProcessOwnerDataBs={
                                            showDataCollectAddCoOwner
                                                ? undefined
                                                : showAddProcessOwner && !isDataCollectCard
                                                  ? `#addProcessOwner${processId}`
                                                  : undefined
                                        }
                                        share={undefined}
                                        shareAction={undefined}
                                        action3={isDataCollectCard ? '' : `${t('words.organize')}`}
                                        theRouteOfAction4=""
                                        deleteProcess={deleteProcess}
                                        action1=""
                                        theRouteOfAction5=""
                                        dataBsTarget={isDataCollectCard ? '' : `#asignProcessToFolder${processId}`}
                                        routeFaq=""
                                        edit={
                                            isDataCollectCard
                                                ? ''
                                                : runOwnerMenuActions
                                                  ? `${t('words.rename')}`
                                                  : ''
                                        }
                                        dataBsTargetEdit={isDataCollectCard ? '' : `#edit${processId}`}
                                        publish={
                                            isDataCollectCard
                                                ? ''
                                                : runOwnerMenuActions
                                                  ? `${t('navigation.markAsFinal')}`
                                                  : ''
                                        }
                                        dataBsTargetPublish={isDataCollectCard ? '' : `#publish${processId}`}
                                        onClick={handleDropdownClick}
                                        rename={
                                            isDataCollectCard && runOwnerMenuActions ? t('words.rename') : undefined
                                        }
                                        renameAction={
                                            isDataCollectCard &&
                                            runOwnerMenuActions &&
                                            onDataCollectRenameRequest
                                                ? () => void onDataCollectRenameRequest()
                                                : undefined
                                        }
                                        action7={
                                            runOwnerMenuActions
                                                ? t('words.delete')
                                                : canWithdrawContributorMenu
                                                  ? t('form.collectWorkspace.withdrawMyContribution', {
                                                        defaultValue: 'Remove my contribution',
                                                    })
                                                  : ''
                                        }
                                        action7OnClick={
                                            runOwnerMenuActions
                                                ? () => setShowDeleteModal(true)
                                                : canWithdrawContributorMenu
                                                  ? () => setShowWithdrawModal(true)
                                                  : undefined
                                        }
                                    />
                                ) : (
                                    <span className="text-secondary" style={{ fontSize: 16, letterSpacing: 1 }}>
                                        &nbsp;
                                    </span>
                                )}
                            </div>
                        </div>
                        <div
                            className={`lib-p-desc${isDataCollectCard ? ' lib-p-desc--datacollect' : ''}`}
                            title={libraryProcessDescription || ''}
                        >
                            {libraryProcessDescription?.length > 0
                                ? isDataCollectCard
                                    ? libraryProcessDescription
                                    : slice(libraryProcessDescription, 110)
                                : '\u00A0'}
                        </div>

                        {/* OWNER — sous la description */}
                        {thisProcessHasAOwner && (
                            <div style={{ marginBottom: '6px' }}>
                                <span className={owner === t('words.me') ? 'lib-owner-me' : 'lib-owner-other'}>
                                    {owner}
                                </span>
                            </div>
                        )}

                        {/* LIGNE INFÉRIEURE : statut à gauche — date à droite */}
                        <div className="lib-p-status-row">
                            <div className="lib-p-status-left">
                                {thisProcessHasAOwner && libraryProcessStatusResolved === 'completed' && (
                                    <span className="home-pill home-pill-done">{t('views.processView.homeStatusCompleted')}</span>
                                )}
                                {thisProcessHasAOwner && libraryProcessStatusResolved === 'ongoing' && (
                                    <span className="home-pill home-pill-ongoing">{t('views.processView.homeStatusOngoing')}</span>
                                )}
                                {thisProcessHasAOwner &&
                                    (libraryProcessStatusResolved === 'new' ||
                                        (!libraryProcessStatusResolved && (thisProcessHasABadge || index === 0))) && (
                                        <span className="lib-pill-new">{t('words.new')}</span>
                                    )}
                            </div>
                            {libraryUpdatedLabel && (
                                <span
                                    className="lib-p-date"
                                    title={`${t('views.processByGroupView.lastModifiedDate')}: ${libraryUpdatedLabel}`}
                                >
                                    {libraryUpdatedLabel}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                {libraryModals}
            </>
        );
    }

    return (
        <>
            <div 
                className='mt-3' 
                style={{ 
                    cursor: isNavigating ? 'wait' : 'pointer',
                    opacity: isNavigating ? 0.8 : 1,
                    transition: 'opacity 0.2s ease',
                    position: 'relative'
                }} 
                onClick={handleCardClick}
            >
                <div
                    className="card text-center shadow-sm process-card unified-process-default-card border-0 position-relative"
                    style={{ ...backgroundStyle, backgroundColor: !bgImage ? 'var(--bg-secondary)' : undefined }}
                    data-bs-toggle={triggerModal ? 'modal' : undefined}
                    data-bs-target={triggerModal ? modalTarget : undefined}
                    data-tutorial={isSample ? 'mapping-swimlanes' : undefined}
                    role="button"
                >
                    {/* [INDICATEUR LOADING] Overlay pendant navigation/switch */}
                    {isNavigating && (
                        <div 
                            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center unified-process-loading-overlay"
                            style={{ 
                                zIndex: 10,
                                borderRadius: 'inherit'
                            }}
                        >
                            <div className="d-flex align-items-center gap-2 unified-process-loading-text">
                                <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <small>
                                    {isSwitching ? t('navigation.switchingCompany') || 'Changement de compagnie...' : t('navigation.loading') || 'Chargement...'}
                                </small>
                            </div>
                        </div>
                    )}
                    
                    <div className="card-body d-flex flex-column justify-content-center align-items-center unified-process-card-overlay rounded h-100 px-3 py-3">
                        <div className={`d-flex justify-content-between w-100 ${(!thisProcessHasAOwner || processName === "Default") && 'mb-3'}`}>
                            <NavLink to={getRoute()} className='text-decoration-none' onClick={(e) => e.preventDefault()}>
                                <span className={`fw-bold ${elementType === 'popular' ? 'text-gradient' : 'unified-process-card-title'}`}>{slice(displayName, 25)}</span>
                                {thisProcessHasABadge &&
                                    <span className='badge rounded-pill bg-primary ms-2'>{theBadgeHasAValue && badgeValue}</span>
                                }
                            </NavLink>
                            <Dropdown
                                dropName=''
                                dropdownType='dropdown dropstart '
                                iconClassName={((role !== 'WORKER' || owner === t('words.me')) && processName !== "Default" && thisProcessHasAOwner) ? 'fa fa-ellipsis-h' : ""}
                                buttonDropClassName={`${((role !== 'WORKER' || owner === t('words.me')) && processName !== "Default" && thisProcessHasAOwner) ? 'btn btn-secondary bg-transparent border-0 ' : 'btn btn-transparent disabled border-0 text-black-custom '}`}
                                styleButton={{}}
                                onMenuButtonClick={onMenuButtonStats}
                                addProcessOwner={
                                    showDataCollectAddCoOwner
                                        ? t('views.processView.addCollectionOwner')
                                        : showAddProcessOwner
                                          ? t('views.processView.addProcessOwner')
                                          : undefined
                                }
                                addProcessOwnerOnClick={
                                    showDataCollectAddCoOwner ? () => setShowCoOwnerModal(true) : undefined
                                }
                                addProcessOwnerDataBs={
                                    showDataCollectAddCoOwner
                                        ? undefined
                                        : showAddProcessOwner
                                          ? `#addProcessOwner${processId}`
                                          : undefined
                                }
                                share={undefined}
                                shareAction={undefined}
                                action3={folder ? '' : isDataCollectCard ? '' : `${t('words.organize')}`}
                                theRouteOfAction4=''
                                deleteProcess={deleteProcess}
                                action1=''
                                theRouteOfAction5=''
                                dataBsTarget={folder || isDataCollectCard ? '' : `#asignProcessToFolder${processId}`}
                                routeFaq=''
                                edit={
                                    isDataCollectCard
                                        ? ''
                                        : showMarkRenameDeleteProcess
                                          ? `${t('words.rename')}`
                                          : ''
                                }
                                dataBsTargetEdit={isDataCollectCard ? '' : `#edit${processId}`}
                                publish={
                                    !folder && showMarkRenameDeleteProcess && !isDataCollectCard
                                        ? `${t('navigation.markAsFinal')}`
                                        : ''
                                }
                                dataBsTargetPublish={isDataCollectCard ? '' : `#publish${processId}`}
                                onClick={handleDropdownClick}
                                rename={
                                    isDataCollectCard && runOwnerMenuActions ? t('words.rename') : undefined
                                }
                                renameAction={
                                    isDataCollectCard &&
                                    runOwnerMenuActions &&
                                    onDataCollectRenameRequest
                                        ? () => void onDataCollectRenameRequest()
                                        : undefined
                                }
                                action7={
                                    runOwnerMenuActions
                                        ? t('words.delete')
                                        : canWithdrawContributorMenu
                                          ? t('form.collectWorkspace.withdrawMyContribution', {
                                                defaultValue: 'Remove my contribution',
                                            })
                                          : ''
                                }
                                action7OnClick={
                                    runOwnerMenuActions
                                        ? () => setShowDeleteModal(true)
                                        : canWithdrawContributorMenu
                                          ? () => setShowWithdrawModal(true)
                                          : undefined
                                }

                            />
                        </div>
                        <p className='text-control text-start small unified-process-card-desc mb-0 w-100  ' style={{ height: '43.26px' }} title={description?.length > 35 ? description : ""}>
                            {description?.length > 0 && !folder && slice(description, 35)}
                        </p>
                        {thisProcessHasAOwner &&
                            <div className='d-flex w-100 mt-2'>
                                <div className='text-primary'>{owner}</div>
                                {index === 0 && <Badge classNamePrent='ms-2' className='rounded-pill text-theme bg-theme-transparent' label={t('words.new')} />}
                            </div>
                        }
                        {notifFolder &&
                            <div className='d-flex w-100 mt-2'>
                                <Badge classNamePrent='ms-2' className='rounded-pill text-theme bg-theme-transparent' label={t('words.new')} />
                            </div>
                        }
                    </div>
                </div>
            </div>

            {processName && processId && <AssignProcessToFolderModal processId={processId} processName={processName} />}
            {processName && processId && <EditprocessModal folder={folder} processId={processId} processName={processName} description={description} setProject={setProject} setProcess={setProcess} />}
            {!folder && (elementType === 'process' || !elementType) && !isDataCollectCard && processId && (
                <AddProcessOwnerModal processId={processId} modalId={`addProcessOwner${processId}`} />
            )}
            {isDataCollectCard && processId && runOwnerMenuActions && showCoOwnerModal && (
                <AddDataCollectionCoOwnerModal
                    collectionId={processId}
                    modalId={`addDataCollectionCoOwner${processId}`}
                    onSuccess={onDataCollectCoOwnerAdded}
                    autoShow
                    onHidden={() => setShowCoOwnerModal(false)}
                />
            )}
            {processId && showDeleteModal && (!isDataCollectCard || runOwnerMenuActions) && (
                <GenericModal
                    title={t('words.delete')}
                    actionButton={t('words.delete')}
                    action={async () => {
                        const r = await Promise.resolve(deleteProcess());
                        if (r === false) return false;
                        return true;
                    }}
                    sentence={
                        isDataCollectCard
                            ? collectIsDraft
                                ? t('form.collectWorkspace.deleteDataCollectionConfirm', {
                                      defaultValue: 'Delete this draft?',
                                  })
                                : t('form.collectWorkspace.deleteDataCollectionConfirmActive', {
                                      defaultValue:
                                          'Delete this collection and all responses? This cannot be undone.',
                                  })
                            : t('views.processView.areYouSure')
                    }
                    processId={processId}
                    modalDomId={isDataCollectCard ? `famla-dc-delete-${processId}` : undefined}
                    autoShow
                    onHidden={() => setShowDeleteModal(false)}
                />
            )}
            {processId && showWithdrawModal && canWithdrawContributorMenu && onWithdrawDataCollectContribution && (
                <GenericModal
                    title={t('form.collectWorkspace.withdrawMyContribution', {
                        defaultValue: 'Remove my contribution',
                    })}
                    actionButton={t('form.collectWorkspace.withdrawMyContribution', {
                        defaultValue: 'Remove my contribution',
                    })}
                    action={async () => {
                        const r = await Promise.resolve(onWithdrawDataCollectContribution());
                        if (r === false) return false;
                        return true;
                    }}
                    sentence={t('form.collectWorkspace.withdrawMyContributionConfirm', {
                        defaultValue:
                            'Remove your contribution from this collection? Your responses and messages will be deleted.',
                    })}
                    processId={processId}
                    modalDomId={`famla-dc-withdraw-${processId}`}
                    autoShow
                    onHidden={() => setShowWithdrawModal(false)}
                />
            )}
            {processId && !isDataCollectCard && <PublishModal processId={processId} />}
        </>
    );
};

export default UnifiedCard;